import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileTextIcon, CheckCircleIcon, AlertCircleIcon, TrendingDownIcon, TrendingUpIcon, BarChart3Icon, CalendarIcon, DollarSignIcon, InfoIcon, ClockIcon, BrainIcon, ActivityIcon, ServerIcon, CheckIcon } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
// Sample data for daily sales over 6 months (180 days)
const generateSalesData = () => {
  const data = [];
  const now = new Date();
  // Base parameters
  const baselineValue = 1200;
  const weekendBoost = 1.4;
  const monthlySeasonality = {
    0: 0.9,
    1: 0.85,
    2: 1.0,
    3: 1.05,
    4: 1.1,
    5: 1.2,
    6: 1.25,
    7: 1.3,
    8: 1.15,
    9: 1.05,
    10: 1.1,
    11: 1.3 // December (holiday season)
  };
  // Special events/promotions (random dates with sales boosts)
  const specialEvents = {
    '2025-02-14': 1.5,
    '2025-03-08': 1.3,
    '2025-05-13': 1.6,
    '2025-06-18': 1.4,
    '2025-07-01': 1.7 // Summer promotion
  };
  // Generate 180 days of data
  for (let i = 180; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dateString = date.toISOString().split('T')[0];
    // Base value with gradual overall growth (8% over 6 months, not linear)
    let growthFactor = 1 + (Math.pow(1.08, (180 - i) / 180) - 1);
    let value = baselineValue * growthFactor;
    // Apply monthly seasonality
    value *= monthlySeasonality[date.getMonth()];
    // Apply day of week pattern
    const day = date.getDay();
    if (day === 5 || day === 6) {
      // Friday and Saturday
      value *= weekendBoost;
    } else if (day === 0) {
      // Sunday
      value *= weekendBoost * 0.9; // Slightly less than Friday/Saturday
    } else if (day === 1) {
      // Monday
      value *= 0.85; // Slowest day
    } else if (day === 4) {
      // Thursday
      value *= 1.1; // Pre-weekend boost
    }
    // Apply special events
    if (specialEvents[dateString]) {
      value *= specialEvents[dateString];
    }
    // Add some randomness (±15%)
    const randomFactor = 0.85 + Math.random() * 0.3;
    value = Math.round(value * randomFactor);
    // Add occasional unexplained spikes or dips (3% chance)
    if (Math.random() < 0.03) {
      const anomalyFactor = Math.random() < 0.5 ? 0.6 + Math.random() * 0.2 : 1.4 + Math.random() * 0.3;
      value = Math.round(value * anomalyFactor);
    }
    data.push({
      date: dateString,
      value
    });
  }
  return data;
};
const salesData = generateSalesData();
const RatingAssessment = () => {
  const navigate = useNavigate();
  const [selectedRating, setSelectedRating] = useState('low');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const handleSubmitRating = () => {
    setShowConfirmation(true);
  };
  const handleConfirmationClose = () => {
    navigate('/dashboard/rating-agency');
  };
  if (showConfirmation) {
    return <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center p-4" data-id="element-2773">
        <div className="w-full max-w-md" data-id="element-2774">
          <Card data-id="element-2775">
            <div className="text-center mb-6" data-id="element-2776">
              <div className="flex justify-center mb-4" data-id="element-2777">
                <div className="w-16 h-16 rounded-full bg-success bg-opacity-10 flex items-center justify-center" data-id="element-2778">
                  <CheckCircleIcon className="w-8 h-8 text-success" data-id="element-2779" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-success" data-id="element-2780">
                Rating Submitted Successfully!
              </h2>
              <p className="text-neutral-500 mt-2" data-id="element-2781">
                The micro-sukuk application has been rated and sent to Shariah
                Advisors for compliance review.
              </p>
            </div>
            <div className="bg-neutral-50 p-4 rounded-lg mb-6" data-id="element-2782">
              <div className="flex items-center justify-between mb-3" data-id="element-2783">
                <h3 className="font-medium" data-id="element-2784">Application Details</h3>
                <span className="bg-success bg-opacity-10 text-success px-2 py-1 rounded-full text-xs font-medium" data-id="element-2785">
                  Low Risk (A)
                </span>
              </div>
              <div className="space-y-2 text-sm" data-id="element-2786">
                <div className="flex justify-between" data-id="element-2787">
                  <span className="text-neutral-400" data-id="element-2788">Business</span>
                  <span className="font-medium" data-id="element-2789">Amani Cafe</span>
                </div>
                <div className="flex justify-between" data-id="element-2790">
                  <span className="text-neutral-400" data-id="element-2791">ID</span>
                  <span className="font-medium" data-id="element-2792">MS2023-104</span>
                </div>
                <div className="flex justify-between" data-id="element-2793">
                  <span className="text-neutral-400" data-id="element-2794">Amount</span>
                  <span className="font-medium" data-id="element-2795">RM 25,000</span>
                </div>
                <div className="flex justify-between" data-id="element-2796">
                  <span className="text-neutral-400" data-id="element-2797">Profit Rate</span>
                  <span className="font-medium" data-id="element-2798">12% p.a.</span>
                </div>
              </div>
            </div>
            <div className="bg-primary bg-opacity-5 border border-primary border-opacity-20 rounded-lg p-4 mb-6" data-id="element-2799">
              <div className="flex items-center" data-id="element-2800">
                <InfoIcon className="w-5 h-5 text-primary mr-2 flex-shrink-0" data-id="element-2801" />
                <p className="text-sm" data-id="element-2802">
                  You'll be notified when the Shariah Advisors complete their
                  compliance review. The review process typically takes 1-2
                  business days.
                </p>
              </div>
            </div>
            <div className="flex justify-center" data-id="element-2803">
              <Button variant="primary" onClick={handleConfirmationClose} data-id="element-2804">
                Return to Dashboard
              </Button>
            </div>
          </Card>
        </div>
      </div>;
  }
  return <div className="max-w-4xl mx-auto" data-id="element-2805">
      <div className="mb-6" data-id="element-2806">
        <h1 className="text-2xl font-bold text-secondary mb-2" data-id="element-2807">
          Risk Rating Assessment
        </h1>
        <p className="text-neutral-400" data-id="element-2808">
          Evaluate micro-sukuk application and assign appropriate risk rating
        </p>
      </div>
      {/* Application Overview */}
      <section className="mb-8" data-id="element-2809">
        <Card data-id="element-2810">
          <div className="flex items-center mb-6" data-id="element-2811">
            <div className="w-12 h-12 rounded-full bg-primary bg-opacity-10 flex items-center justify-center mr-4" data-id="element-2812">
              <FileTextIcon className="w-6 h-6 text-primary" data-id="element-2813" />
            </div>
            <div data-id="element-2814">
              <h3 className="font-bold text-lg" data-id="element-2815">Application #MS2023-104</h3>
              <div className="flex items-center text-sm text-neutral-400" data-id="element-2816">
                <span data-id="element-2817">Amani Cafe</span>
                <span className="mx-2" data-id="element-2818">•</span>
                <span data-id="element-2819">Equipment Purchase</span>
                <span className="mx-2" data-id="element-2820">•</span>
                <span data-id="element-2821">RM 25,000</span>
              </div>
            </div>
            <div className="ml-auto bg-warning bg-opacity-10 text-warning px-3 py-1 rounded-full text-sm font-medium" data-id="element-2822">
              Pending Rating
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2823">
            <div data-id="element-2824">
              <h4 className="font-medium mb-3" data-id="element-2825">Business Information</h4>
              <div className="space-y-3" data-id="element-2826">
                <div className="flex justify-between" data-id="element-2827">
                  <span className="text-sm text-neutral-400" data-id="element-2828">
                    Business Name
                  </span>
                  <span className="text-sm font-medium" data-id="element-2829">Amani Cafe</span>
                </div>
                <div className="flex justify-between" data-id="element-2830">
                  <span className="text-sm text-neutral-400" data-id="element-2831">
                    Registration No.
                  </span>
                  <span className="text-sm font-medium" data-id="element-2832">BRN-1234567-A</span>
                </div>
                <div className="flex justify-between" data-id="element-2833">
                  <span className="text-sm text-neutral-400" data-id="element-2834">
                    Years in Operation
                  </span>
                  <span className="text-sm font-medium" data-id="element-2835">3 years, 4 months</span>
                </div>
                <div className="flex justify-between" data-id="element-2836">
                  <span className="text-sm text-neutral-400" data-id="element-2837">
                    Monthly Revenue
                  </span>
                  <span className="text-sm font-medium" data-id="element-2838">RM 45,000</span>
                </div>
                <div className="flex justify-between" data-id="element-2839">
                  <span className="text-sm text-neutral-400" data-id="element-2840">Credit Score</span>
                  <span className="text-sm font-medium text-success" data-id="element-2841">
                    Excellent (85/100)
                  </span>
                </div>
              </div>
            </div>
            <div data-id="element-2842">
              <h4 className="font-medium mb-3" data-id="element-2843">Financing Request</h4>
              <div className="space-y-3" data-id="element-2844">
                <div className="flex justify-between" data-id="element-2845">
                  <span className="text-sm text-neutral-400" data-id="element-2846">Purpose</span>
                  <span className="text-sm font-medium" data-id="element-2847">
                    Equipment Purchase
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2848">
                  <span className="text-sm text-neutral-400" data-id="element-2849">
                    Amount Requested
                  </span>
                  <span className="text-sm font-medium" data-id="element-2850">RM 25,000</span>
                </div>
                <div className="flex justify-between" data-id="element-2851">
                  <span className="text-sm text-neutral-400" data-id="element-2852">Duration</span>
                  <span className="text-sm font-medium" data-id="element-2853">6 months</span>
                </div>
                <div className="flex justify-between" data-id="element-2854">
                  <span className="text-sm text-neutral-400" data-id="element-2855">
                    Monthly Payment
                  </span>
                  <span className="text-sm font-medium" data-id="element-2856">RM 4,271</span>
                </div>
                <div className="flex justify-between" data-id="element-2857">
                  <span className="text-sm text-neutral-400" data-id="element-2858">Total Profit</span>
                  <span className="text-sm font-medium" data-id="element-2859">
                    RM 2,250 (18% p.a.)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </section>
      {/* AI Eligibility Analysis */}
      <section className="mb-8" data-id="element-2860">
        <Card data-id="element-2861">
          <div className="flex items-center mb-6" data-id="element-2862">
            <div className="w-12 h-12 rounded-full bg-secondary bg-opacity-10 flex items-center justify-center mr-4" data-id="element-2863">
              <BrainIcon className="w-6 h-6 text-secondary" data-id="element-2864" />
            </div>
            <div data-id="element-2865">
              <h3 className="font-bold text-lg" data-id="element-2866">AI Eligibility Analysis</h3>
              <p className="text-sm text-neutral-400" data-id="element-2867">
                Based on transaction data from cashless payment systems
              </p>
            </div>
            <div className="ml-auto bg-success bg-opacity-10 text-success px-3 py-1 rounded-full text-sm font-medium" data-id="element-2868">
              Eligible
            </div>
          </div>
          {/* Sales Chart */}
          <div className="mb-6" data-id="element-2869">
            <h4 className="font-medium mb-3" data-id="element-2870">Daily Sales (Last 6 Months)</h4>
            <div className="bg-neutral-50 p-3 rounded-lg h-64 relative" data-id="element-2871">
              {/* Chart container */}
              <div className="absolute inset-0 p-3" data-id="element-2872">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-0 w-10 flex flex-col justify-between text-xs text-neutral-400" data-id="element-2873">
                  <span data-id="element-2874">RM 2,500</span>
                  <span data-id="element-2875">RM 2,000</span>
                  <span data-id="element-2876">RM 1,500</span>
                  <span data-id="element-2877">RM 1,000</span>
                  <span data-id="element-2878">RM 500</span>
                  <span data-id="element-2879">RM 0</span>
                </div>
                {/* Chart area */}
                <div className="absolute left-12 right-0 top-0 bottom-0" data-id="element-2880">
                  {/* Chart with bars */}
                  <svg className="w-full h-full" viewBox="0 0 1000 300" preserveAspectRatio="none" data-id="element-2881">
                    {/* Grid lines */}
                    <line x1="0" y1="0" x2="1000" y2="0" stroke="#e5e7eb" strokeWidth="1" data-id="element-2882" />
                    <line x1="0" y1="60" x2="1000" y2="60" stroke="#e5e7eb" strokeWidth="1" data-id="element-2883" />
                    <line x1="0" y1="120" x2="1000" y2="120" stroke="#e5e7eb" strokeWidth="1" data-id="element-2884" />
                    <line x1="0" y1="180" x2="1000" y2="180" stroke="#e5e7eb" strokeWidth="1" data-id="element-2885" />
                    <line x1="0" y1="240" x2="1000" y2="240" stroke="#e5e7eb" strokeWidth="1" data-id="element-2886" />
                    <line x1="0" y1="300" x2="1000" y2="300" stroke="#e5e7eb" strokeWidth="1" data-id="element-2887" />
                    {/* Bar chart - we'll show weekly data points to avoid overcrowding */}
                    {salesData.filter((_, index) => index % 7 === 0).map((dataPoint, index, filteredArray) => {
                    // Calculate bar width (slightly less than full width to have spacing)
                    const barWidth = 1000 / filteredArray.length * 0.7;
                    // Calculate bar position
                    const xPosition = index * (1000 / filteredArray.length) + 1000 / filteredArray.length * 0.15;
                    // Calculate bar height (max value is 2500, height is 300px)
                    const barHeight = dataPoint.value / 2500 * 300;
                    // Calculate y position (SVG y starts from top)
                    const yPosition = 300 - barHeight;
                    return <rect key={index} x={xPosition} y={yPosition} width={barWidth} height={barHeight} fill={dataPoint.value > 1800 ? 'rgba(0, 130, 81, 0.8)' : 'rgba(0, 130, 81, 0.5)'} data-id="element-2888" />;
                  })}
                    {/* Trend line - 3-point moving average */}
                    <path d={salesData.filter((_, index) => index % 7 === 0).reduce((path, dataPoint, index, filteredArray) => {
                    const xPosition = index * (1000 / filteredArray.length) + 1000 / filteredArray.length * 0.5;
                    const yPosition = 300 - dataPoint.value / 2500 * 300;
                    return index === 0 ? `M ${xPosition},${yPosition}` : `${path} L ${xPosition},${yPosition}`;
                  }, '')} fill="none" stroke="#008251" strokeWidth="2" strokeDasharray="5,5" data-id="element-2889" />
                  </svg>
                  {/* X-axis labels */}
                  <div className="absolute left-0 right-0 bottom-0 transform translate-y-6 flex justify-between text-xs text-neutral-400" data-id="element-2890">
                    <span data-id="element-2891">Jan</span>
                    <span data-id="element-2892">Feb</span>
                    <span data-id="element-2893">Mar</span>
                    <span data-id="element-2894">Apr</span>
                    <span data-id="element-2895">May</span>
                    <span data-id="element-2896">Jun</span>
                    <span data-id="element-2897">Jul</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* AI Analysis Results */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pt-6" data-id="element-2898">
            <div data-id="element-2899">
              <h4 className="font-medium mb-3" data-id="element-2900">AI Analysis Results</h4>
              <div className="bg-neutral-50 rounded-lg p-4" data-id="element-2901">
                <div className="flex items-center justify-between mb-4" data-id="element-2902">
                  <div className="flex items-center" data-id="element-2903">
                    <div className="w-2 h-2 rounded-full bg-success mr-2" data-id="element-2904"></div>
                    <span className="font-medium" data-id="element-2905">Assessment Summary</span>
                  </div>
                  <div className="flex items-center" data-id="element-2906">
                    <span className="text-sm text-success font-medium mr-1" data-id="element-2907">
                      87%
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-2908">
                      Confidence Score
                    </span>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-2909">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-neutral-100" data-id="element-2910">
                    <div data-id="element-2911">
                      <div className="text-sm text-neutral-400 mb-1" data-id="element-2912">
                        Application Status
                      </div>
                      <div className="font-medium text-success" data-id="element-2913">Eligible</div>
                    </div>
                    <div className="text-right" data-id="element-2914">
                      <div className="text-sm text-neutral-400 mb-1" data-id="element-2915">
                        Risk Assessment
                      </div>
                      <div className="font-medium text-success" data-id="element-2916">Low Risk</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-neutral-100" data-id="element-2917">
                    <div data-id="element-2918">
                      <div className="text-sm text-neutral-400 mb-1" data-id="element-2919">
                        Recommended Financing Limit
                      </div>
                      <div className="font-medium" data-id="element-2920">RM 50,000</div>
                      <div className="text-xs text-success" data-id="element-2921">
                        2x requested amount
                      </div>
                    </div>
                    <div className="text-right" data-id="element-2922">
                      <div className="text-sm text-neutral-400 mb-1" data-id="element-2923">
                        Risk Anomalies
                      </div>
                      <div className="font-medium text-success" data-id="element-2924">
                        None Detected
                      </div>
                      <div className="text-xs text-success" data-id="element-2925">
                        All checks passed
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-xs text-neutral-500" data-id="element-2926">
                  Based on analysis of 180 days of transaction data, business
                  performance metrics, and market conditions.
                </div>
              </div>
            </div>
            <div data-id="element-2927">
              <h4 className="font-medium mb-3" data-id="element-2928">Key Insights</h4>
              <div className="space-y-3" data-id="element-2929">
                <div className="flex items-start" data-id="element-2930">
                  <div className="w-8 h-8 rounded-full bg-success bg-opacity-10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-2931">
                    <ActivityIcon className="w-4 h-4 text-success" data-id="element-2932" />
                  </div>
                  <div data-id="element-2933">
                    <div className="font-medium" data-id="element-2934">Growth Trajectory</div>
                    <p className="text-sm text-neutral-400" data-id="element-2935">
                      Business has shown consistent 8% monthly growth over the
                      past 6 months, indicating strong market position and
                      customer loyalty.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2936">
                  <div className="w-8 h-8 rounded-full bg-success bg-opacity-10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-2937">
                    <TrendingDownIcon className="w-4 h-4 text-success" data-id="element-2938" />
                  </div>
                  <div data-id="element-2939">
                    <div className="font-medium" data-id="element-2940">Operational Stability</div>
                    <p className="text-sm text-neutral-400" data-id="element-2941">
                      Low volatility in daily sales (15% coefficient of
                      variation) demonstrates operational resilience and
                      predictable cash flow.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2942">
                  <div className="w-8 h-8 rounded-full bg-success bg-opacity-10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-2943">
                    <CalendarIcon className="w-4 h-4 text-success" data-id="element-2944" />
                  </div>
                  <div data-id="element-2945">
                    <div className="font-medium" data-id="element-2946">Weekend Performance</div>
                    <p className="text-sm text-neutral-400" data-id="element-2947">
                      Strong weekend sales (greater than 30% above weekly
                      average) indicate effective business model and customer
                      demand during peak periods.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-secondary bg-opacity-5 border border-secondary border-opacity-20 rounded-lg p-4" data-id="element-2948">
            <div className="flex items-start" data-id="element-2949">
              <ServerIcon className="w-5 h-5 text-secondary mr-2 mt-0.5 flex-shrink-0" data-id="element-2950" />
              <div data-id="element-2951">
                <h5 className="text-sm font-medium text-secondary mb-1" data-id="element-2952">
                  AI Engine Assessment
                </h5>
                <p className="text-sm text-neutral-500 mb-2" data-id="element-2953">
                  Our AI Engine analyzed 180 days of transaction data from the
                  MSME's cashless payment systems. The analysis confirms strong
                  business fundamentals with high confidence (87%) and
                  recommends a financing limit of RM 50,000 based on consistent
                  growth patterns and stable operations.
                </p>
                <div className="flex items-center text-sm text-success" data-id="element-2954">
                  <CheckIcon className="w-4 h-4 mr-1" data-id="element-2955" />
                  <span data-id="element-2956">
                    No anomalies or suspicious transaction patterns detected
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </section>
      {/* Financial Analysis */}
      <section className="mb-8" data-id="element-2957">
        <Card data-id="element-2958">
          <h3 className="font-bold text-lg mb-4" data-id="element-2959">Financial Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2960">
            <div data-id="element-2961">
              <h4 className="font-medium mb-3" data-id="element-2962">Revenue & Profitability</h4>
              <div className="space-y-3" data-id="element-2963">
                <div className="flex justify-between" data-id="element-2964">
                  <span className="text-sm text-neutral-400" data-id="element-2965">
                    Monthly Revenue
                  </span>
                  <span className="text-sm font-medium" data-id="element-2966">RM 45,000</span>
                </div>
                <div className="flex justify-between" data-id="element-2967">
                  <span className="text-sm text-neutral-400" data-id="element-2968">
                    Monthly Expenses
                  </span>
                  <span className="text-sm font-medium" data-id="element-2969">RM 32,000</span>
                </div>
                <div className="flex justify-between" data-id="element-2970">
                  <span className="text-sm text-neutral-400" data-id="element-2971">
                    Net Monthly Profit
                  </span>
                  <span className="text-sm font-medium text-success" data-id="element-2972">
                    RM 13,000
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2973">
                  <span className="text-sm text-neutral-400" data-id="element-2974">
                    Profit Margin
                  </span>
                  <span className="text-sm font-medium text-success" data-id="element-2975">
                    28.9%
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2976">
                  <span className="text-sm text-neutral-400" data-id="element-2977">
                    Year-on-Year Growth
                  </span>
                  <span className="text-sm font-medium" data-id="element-2978">22%</span>
                </div>
              </div>
            </div>
            <div data-id="element-2979">
              <h4 className="font-medium mb-3" data-id="element-2980">Debt & Repayment Capacity</h4>
              <div className="space-y-3" data-id="element-2981">
                <div className="flex justify-between" data-id="element-2982">
                  <span className="text-sm text-neutral-400" data-id="element-2983">
                    Existing Monthly Debt
                  </span>
                  <span className="text-sm font-medium" data-id="element-2984">RM 3,500</span>
                </div>
                <div className="flex justify-between" data-id="element-2985">
                  <span className="text-sm text-neutral-400" data-id="element-2986">
                    New Monthly Payment
                  </span>
                  <span className="text-sm font-medium" data-id="element-2987">RM 4,271</span>
                </div>
                <div className="flex justify-between" data-id="element-2988">
                  <span className="text-sm text-neutral-400" data-id="element-2989">
                    Total Monthly Debt
                  </span>
                  <span className="text-sm font-medium" data-id="element-2990">RM 7,771</span>
                </div>
                <div className="flex justify-between" data-id="element-2991">
                  <span className="text-sm text-neutral-400" data-id="element-2992">
                    Debt-to-Income Ratio
                  </span>
                  <span className="text-sm font-medium text-success" data-id="element-2993">
                    17.3%
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2994">
                  <span className="text-sm text-neutral-400" data-id="element-2995">
                    Debt Service Coverage
                  </span>
                  <span className="text-sm font-medium text-success" data-id="element-2996">
                    1.67x
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2997">
            <div className="bg-neutral-50 p-4 rounded-lg" data-id="element-2998">
              <h4 className="font-medium mb-3" data-id="element-2999">Financial Strengths</h4>
              <ul className="space-y-2 text-sm" data-id="element-3000">
                <li className="flex items-center" data-id="element-3001">
                  <CheckCircleIcon className="w-4 h-4 text-success mr-2 flex-shrink-0" data-id="element-3002" />
                  <span data-id="element-3003">Strong profit margin (28.9%)</span>
                </li>
                <li className="flex items-center" data-id="element-3004">
                  <CheckCircleIcon className="w-4 h-4 text-success mr-2 flex-shrink-0" data-id="element-3005" />
                  <span data-id="element-3006">Low debt-to-income ratio (17.3%)</span>
                </li>
                <li className="flex items-center" data-id="element-3007">
                  <CheckCircleIcon className="w-4 h-4 text-success mr-2 flex-shrink-0" data-id="element-3008" />
                  <span data-id="element-3009">Consistent revenue growth (22% YoY)</span>
                </li>
                <li className="flex items-center" data-id="element-3010">
                  <CheckCircleIcon className="w-4 h-4 text-success mr-2 flex-shrink-0" data-id="element-3011" />
                  <span data-id="element-3012">Good debt service coverage (1.67x)</span>
                </li>
              </ul>
            </div>
            <div className="bg-neutral-50 p-4 rounded-lg" data-id="element-3013">
              <h4 className="font-medium mb-3" data-id="element-3014">Financial Concerns</h4>
              <ul className="space-y-2 text-sm" data-id="element-3015">
                <li className="flex items-center" data-id="element-3016">
                  <AlertCircleIcon className="w-4 h-4 text-warning mr-2 flex-shrink-0" data-id="element-3017" />
                  <span data-id="element-3018">Seasonal business fluctuations</span>
                </li>
                <li className="flex items-center" data-id="element-3019">
                  <AlertCircleIcon className="w-4 h-4 text-warning mr-2 flex-shrink-0" data-id="element-3020" />
                  <span data-id="element-3021">Increasing competition in the area</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </section>
      {/* Risk Rating Assignment */}
      <section className="mb-8" data-id="element-3022">
        <Card data-id="element-3023">
          <h3 className="font-bold text-lg mb-4" data-id="element-3024">Risk Rating Assignment</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-3025">
            <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedRating === 'low' ? 'border-success bg-success bg-opacity-5' : 'border-neutral-200 hover:border-success'}`} onClick={() => setSelectedRating('low')} data-id="element-3026">
              <div className="flex items-center mb-3" data-id="element-3027">
                <div className="w-8 h-8 rounded-full bg-success bg-opacity-10 flex items-center justify-center mr-2" data-id="element-3028">
                  <TrendingDownIcon className="w-4 h-4 text-success" data-id="element-3029" />
                </div>
                <h4 className="font-medium" data-id="element-3030">Low Risk (A)</h4>
              </div>
              <div className="space-y-2 text-sm" data-id="element-3031">
                <div className="flex justify-between" data-id="element-3032">
                  <span className="text-neutral-400" data-id="element-3033">Profit Rate</span>
                  <span className="font-medium" data-id="element-3034">12% p.a.</span>
                </div>
                <div className="flex justify-between" data-id="element-3035">
                  <span className="text-neutral-400" data-id="element-3036">Default Probability</span>
                  <span className="font-medium" data-id="element-3037">0.8%</span>
                </div>
              </div>
            </div>
            <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedRating === 'medium' ? 'border-warning bg-warning bg-opacity-5' : 'border-neutral-200 hover:border-warning'}`} onClick={() => setSelectedRating('medium')} data-id="element-3038">
              <div className="flex items-center mb-3" data-id="element-3039">
                <div className="w-8 h-8 rounded-full bg-warning bg-opacity-10 flex items-center justify-center mr-2" data-id="element-3040">
                  <BarChart3Icon className="w-4 h-4 text-warning" data-id="element-3041" />
                </div>
                <h4 className="font-medium" data-id="element-3042">Medium Risk (B)</h4>
              </div>
              <div className="space-y-2 text-sm" data-id="element-3043">
                <div className="flex justify-between" data-id="element-3044">
                  <span className="text-neutral-400" data-id="element-3045">Profit Rate</span>
                  <span className="font-medium" data-id="element-3046">16% p.a.</span>
                </div>
                <div className="flex justify-between" data-id="element-3047">
                  <span className="text-neutral-400" data-id="element-3048">Default Probability</span>
                  <span className="font-medium" data-id="element-3049">3.2%</span>
                </div>
              </div>
            </div>
            <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedRating === 'high' ? 'border-error bg-error bg-opacity-5' : 'border-neutral-200 hover:border-error'}`} onClick={() => setSelectedRating('high')} data-id="element-3050">
              <div className="flex items-center mb-3" data-id="element-3051">
                <div className="w-8 h-8 rounded-full bg-error bg-opacity-10 flex items-center justify-center mr-2" data-id="element-3052">
                  <TrendingUpIcon className="w-4 h-4 text-error" data-id="element-3053" />
                </div>
                <h4 className="font-medium" data-id="element-3054">High Risk (C)</h4>
              </div>
              <div className="space-y-2 text-sm" data-id="element-3055">
                <div className="flex justify-between" data-id="element-3056">
                  <span className="text-neutral-400" data-id="element-3057">Profit Rate</span>
                  <span className="font-medium" data-id="element-3058">20% p.a.</span>
                </div>
                <div className="flex justify-between" data-id="element-3059">
                  <span className="text-neutral-400" data-id="element-3060">Default Probability</span>
                  <span className="font-medium" data-id="element-3061">8.5%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 p-4 rounded-lg mb-6" data-id="element-3062">
            <h4 className="font-medium mb-3" data-id="element-3063">Rating Justification</h4>
            <textarea className="w-full border border-neutral-200 rounded-lg py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" rows={3} placeholder="Provide reasoning for the assigned risk rating..." defaultValue="Amani Cafe demonstrates strong financial performance with a healthy profit margin of 28.9% and low debt-to-income ratio of 17.3%. The business has been operating for over 3 years with consistent growth. The credit score is excellent (85/100) with no history of defaults. The equipment purchase is well-aligned with their business model and will likely contribute to increased revenue. Based on these factors, a Low Risk (A) rating is appropriate." data-id="element-3064"></textarea>
          </div>
          <div className="bg-primary bg-opacity-5 border border-primary border-opacity-20 rounded-lg p-4 mb-6" data-id="element-3065">
            <div className="flex items-center mb-2" data-id="element-3066">
              <InfoIcon className="w-5 h-5 text-primary mr-2" data-id="element-3067" />
              <h4 className="font-medium text-primary" data-id="element-3068">Rating Impact</h4>
            </div>
            <p className="text-sm text-neutral-500 mb-3" data-id="element-3069">
              The assigned rating directly impacts the profit rate offered to
              the MSME and influences investor interest. Low Risk (A)
              micro-sukuk are typically funded 2.4x faster than Medium Risk (B)
              offerings.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-3" data-id="element-3070">
              <div data-id="element-3071">
                <div className="text-xs text-neutral-400 mb-1" data-id="element-3072">
                  Current Profit Rate
                </div>
                <div className="font-medium" data-id="element-3073">18% p.a.</div>
              </div>
              <div data-id="element-3074">
                <div className="text-xs text-neutral-400 mb-1" data-id="element-3075">
                  New Profit Rate (A)
                </div>
                <div className="font-medium text-success" data-id="element-3076">12% p.a.</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-3" data-id="element-3077">
            <Button variant="outline" className="border-neutral-300 text-neutral-500" data-id="element-3078">
              Request Additional Information
            </Button>
            <Button variant="primary" onClick={handleSubmitRating} data-id="element-3079">
              Submit Rating
            </Button>
          </div>
        </Card>
      </section>
    </div>;
};
export default RatingAssessment;